import type { IncidentStatus } from '@eppendorf/vnls-notifications-utils';

export const DASHBOARD_TILE_IDS = [
  'labHealth',
  'deviceOverview',
  'recentDeviations',
  'incidents',
  'auditTrail',
  'pendingInvitations',
  'reports',
] as const;

export type DashboardTileId = (typeof DASHBOARD_TILE_IDS)[number];

export interface DashboardSelectedDevice {
  manufacturer: string;
  serialNumber: string;
  telemetryIds: string[];
}

export interface DashboardPreferences {
  version: 1;
  hiddenTileIds: DashboardTileId[];
  selectedDevices: DashboardSelectedDevice[];
}

export interface DashboardHealthDeviceState extends DashboardMonitoringDevice {
  aggregateAlertState?: string | null;
}

export interface DashboardLabHealth {
  healthPercentage: number | null;
  healthyDevices: number;
  devicesNeedingAttention: number;
  unknownDevices: number;
  pairedDevices: number;
  monitoringHealth: DashboardMonitoringHealth;
}

export interface DashboardMonitoringHealth {
  devicesNeedingAttention: number;
  alertDevices: number;
  warningDevices: number;
  unavailableDevices: number;
  noCurrentStatusDevices: number;
  normalDevices: number;
  alertDeviceDetails: DashboardMonitoringDevice[];
  warningDeviceDetails: DashboardMonitoringDevice[];
  unavailableDeviceDetails: DashboardMonitoringDevice[];
  normalDeviceDetails: DashboardMonitoringDevice[];
}

export interface DashboardMonitoringDevice {
  manufacturer?: string;
  serialNumber?: string;
  name?: string;
  model?: string;
  type?: string;
  connectionStatus?: string | null;
  activityState?: string | null;
}

export interface DashboardFleetStatus {
  totalDevices: number;
  pairedDevices: number;
  pendingDevices: number;
  readyToPairDevices: number;
  waitingForMetadataDevices: number;
  unclassifiedDevices: number;
}

export interface DashboardDeviceStatus extends DashboardMonitoringDevice {
  activityState?: string | null;
}

export interface DashboardDeviceStatusSummary {
  totalDevices: number;
  connectedDevices: number;
  pendingDevices: number;
  unavailableDevices: number;
  pendingDeviceDetails: DashboardDeviceStatus[];
  unavailableDeviceDetails: DashboardDeviceStatus[];
}

export interface DashboardDeviceMetadata {
  manufacturer: string;
  serialNumber: string;
  name: string;
  location?: string;
}

export interface RecentDeviation {
  id: string;
  severity: 'error' | 'alert' | 'warning';
  message: string;
  deviceName: string;
  manufacturer: string;
  serialNumber: string;
  location?: string;
  timestamp: string;
  duration:
    | { status: 'resolved'; milliseconds: number }
    | { status: 'ongoing'; milliseconds: number }
    | { status: 'unknown' };
}

export interface RecentDeviations {
  total: number;
  items: RecentDeviation[];
}

export interface AlertIncident {
  id: string;
  severity: 'error' | 'alert' | 'warning' | 'info';
  incidentStatus?: IncidentStatus;
  description: string;
  deviceName: string;
  location?: string;
  timestamp: number;
}

export interface PendingInvitation {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  inviteSentAt: string;
}

export interface LatestReport {
  id: string;
  name: string;
  createdAt: string;
  size: string;
  reportType: 'stability' | 'audit-trail';
  downloadUrl?: string;
}
